"use client";

import { useEffect, useState } from "react";
import { Menu, Search, X } from "lucide-react";
import { CartIcon } from "@/components/shop/CartIcon";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer({
  scrolled
}: {
  scrolled: boolean;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        <div className="h-3 w-16 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-3 w-16 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-3 w-16 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-3 w-16 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-3 w-16 bg-muted/40 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8 lg:gap-10">
        {menuItems.map((item, i) => <NavigationMenuItem data-index={i} key={item.id}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={`group relative inline-flex h-9 items-center bg-transparent px-0 py-2 text-[12px] uppercase font-default font-bold hover:bg-transparent focus:bg-transparent transition-colors duration-300 ${scrolled ? "text-foreground" : "text-foreground"}`} style={{
            letterSpacing: "0.18em"
          }}>
                <span className="relative">
                  {item.label}
                  <span className="pointer-events-none absolute -bottom-1 left-0 h-px w-0 transition-all duration-500 ease-out group-hover:w-full" style={{
                backgroundColor: "var(--chart-1)"
              }} />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 px-8 mt-12">
        <div className="h-10 w-3/4 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-10 w-2/3 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-10 w-3/5 bg-muted/40 animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-2 px-8 mt-16">
      {menuItems.map((item, i) => <Link data-index={i} key={item.id} to={item.href || "#"} onClick={onClose} className="group flex items-baseline gap-5 border-b border-border/60 py-5 transition-colors hover:opacity-80">
          <span className="text-xs font-mono font-medium tabular-nums" style={{
        color: "var(--chart-1)",
        letterSpacing: "0.15em"
      }}>
            {String(i + 1).padStart(2, "0")}
          </span>
          <span className="font-default text-3xl font-normal tracking-tight text-foreground">
            {item.label}
          </span>
        </Link>)}
    </nav>;
}
export default function TheEssenceHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      if (href === "/#") {
        e.preventDefault();
        window.scrollTo({
          top: 0,
          behavior: "smooth"
        });
        return;
      }
      let hash = "";
      if (href.startsWith("#")) {
        hash = href;
      } else if (href.startsWith("/") && href.includes("#")) {
        const [path, hashPart] = href.split("#");
        if (hashPart === "") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        if (path === "/" || path === window.location.pathname) {
          hash = "#" + hashPart;
        }
      }
      if (hash && hash !== "#") {
        const element = document.querySelector(hash);
        if (element) {
          e.preventDefault();
          element.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.currentTarget.value);
  };
  return <section data-section-id="4363" id="the-essence-header" className={`sticky top-0 z-50 w-full bg-background text-foreground transition-all duration-[400ms] ease-out ${scrolled ? "bg-background/95 backdrop-blur-md border-b border-border" : "bg-transparent border-b border-transparent"}`}>
      <div className="mx-auto flex h-[88px] w-full items-center justify-between px-6 sm:px-10 lg:px-16">
        {}
        <div className="flex items-center">
          <Link to="/" aria-label="Essence & Co. home" className="flex items-center">
            <WvcLogo className="h-7 w-auto" />
          </Link>
        </div>

        {}
        <div className="hidden lg:flex flex-1 justify-center">
          <MenuProvider menu_id="24" className="header-menu">
            <DesktopMenuConsumer scrolled={scrolled} />
          </MenuProvider>
        </div>

        {}
        <div className="hidden lg:flex items-center gap-5">
          <div className="h-6 w-px" style={{
          backgroundColor: "var(--border)"
        }} aria-hidden="true" />

          {}
          <Button variant="ghost" size="icon" onClick={() => setSearchOpen(s => !s)} aria-label="Toggle search" className="h-9 w-9 hover:bg-transparent hover:opacity-70">
            {searchOpen ? <X className="size-[18px]" strokeWidth={1.25} /> : <Search className="size-[18px]" strokeWidth={1.25} />}
          </Button>

          <div className="h-6 w-px" style={{
          backgroundColor: "var(--border)"
        }} aria-hidden="true" />

          {}
          <CartIcon variant="ghost" size="icon" iconSize="size-[18px]" className="h-9 w-9 hover:bg-transparent hover:opacity-70" />
        </div>

        {}
        <div className="flex lg:hidden items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => setSearchOpen(s => !s)} aria-label="Toggle search" className="h-9 w-9 hover:bg-transparent hover:opacity-70">
            <Search className="size-5" strokeWidth={1.25} />
          </Button>

          <CartIcon variant="ghost" size="icon" iconSize="size-5" className="h-9 w-9 hover:bg-transparent hover:opacity-70" />

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu" className="h-9 w-9 hover:bg-transparent hover:opacity-70">
                <Menu className="size-5" strokeWidth={1.25} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md bg-background border-l border-border p-0">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <div className="flex h-[88px] items-center px-8 border-b border-border">
                <Link to="/" onClick={() => setMobileOpen(false)}>
                  <WvcLogo className="h-7 w-auto" />
                </Link>
              </div>

              <MenuProvider menu_id="24" className="mobile-menu">
                <MobileMenuConsumer onClose={() => setMobileOpen(false)} />
              </MenuProvider>

              <div className="px-8 mt-12">
                <p className="text-[11px] font-default uppercase font-bold" style={{
                letterSpacing: "0.22em",
                color: "var(--muted-foreground)"
              }}>
                  Inquiries
                </p>
                <p className="mt-3 font-default text-base text-foreground">
                  concierge@essence.co
                </p>
                <p className="mt-1 font-default text-base text-foreground">
                  +1 (800) 555-SCENT
                </p>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {}
      <div className={`overflow-hidden border-border transition-all duration-400 ease-out ${searchOpen ? "max-h-32 border-t opacity-100" : "max-h-0 border-t-0 opacity-0"}`}>
        <div className="mx-auto px-6 sm:px-10 lg:px-16 py-5">
          <div className="flex items-center gap-4 border-b border-border pb-3">
            <Search className="size-[18px] opacity-60" strokeWidth={1.25} aria-hidden="true" />
            <input type="text" placeholder="Discover your signature scent..." value={searchQuery} onChange={handleSearchChange} className="flex-1 bg-transparent font-default text-lg text-foreground placeholder:opacity-50 placeholder:font-default outline-none border-0" autoFocus={searchOpen} />
            <span className="text-[11px] font-default uppercase font-bold" style={{
            letterSpacing: "0.22em",
            color: "var(--muted-foreground)"
          }}>
              Enter to search
            </span>
          </div>
        </div>
      </div>
    </section>;
}